import React, { useEffect, useState } from 'react';
import {
	Button,
	Grid,
	MenuItem,
	Paper,
	Select,
	Typography,
	FormControl,
	InputLabel,
	TextField,
	FormControlLabel,
	Switch,
	Divider,
	Tooltip,
	IconButton,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
	apiGetAllWorkerNames,
	apiGetAllClient,
	apiGetSispatByWorker,
	apiCreateSispat,
	apiUpdateSispat,
	IClientBackEnd,
	IWorkerNameBackEnd,
	ISispat,
	ISispatOutput,
	apiGetAllPlatforms,
	IPlatformOnlyBackEnd,
	apiGetAllSispat,
} from '../../services/apiService';
import { existInVisibilityRoleInLocalStorage } from '../../utils/existInVisibilityRoleInLocalStorage';
import { green } from '@mui/material/colors';

interface NewSispat {
	clientId: number;
	sispatNumber: number;
	status: boolean;
	statusDescription: string;
	platformId: number | null;
}

const SispatModule: React.FC = () => {
	const [workerNames, setWorkerNames] = useState<IWorkerNameBackEnd[]>([]);
	const [selectedWorkerId, setSelectedWorkerId] = useState<number | ''>('');
	const [sispats, setSispats] = useState<ISispatOutput[]>([]);
	const [allClients, setAllClients] = useState<IClientBackEnd[]>([]);
	const [newSispat, setNewSispat] = useState<NewSispat>({
		clientId: 0,
		sispatNumber: 0,
		status: true,
		statusDescription: '',
		platformId: null,
	});
	const [hasSearched, setHasSearched] = useState(false);
	const [hideAddButton, setHideAddButton] = useState(false);
	const [hideUpdateButton, setHideUpdateButton] = useState(false);

	const [allPlatforms, setAllPlatforms] = useState<IPlatformOnlyBackEnd[]>([]);
	const [filteredPlatforms, setFilteredPlatforms] = useState<
		IPlatformOnlyBackEnd[]
	>([]);

	useEffect(() => {
		setHideAddButton(
			existInVisibilityRoleInLocalStorage('hide add sispat button')
		);
		setHideUpdateButton(
			existInVisibilityRoleInLocalStorage('hide update sispat button')
		);

		(async () => {
			try {
				const [names, clients, plats] = await Promise.all([
					apiGetAllWorkerNames(),
					apiGetAllClient(),
					apiGetAllPlatforms(),
				]);
				setWorkerNames(
					names.sort((a, b) => a.worker_name.localeCompare(b.worker_name))
				);
				setAllClients(
					clients.sort((a, b) => a.client_name.localeCompare(b.client_name))
				);
				setAllPlatforms(plats);
				setFilteredPlatforms(plats); // inicialmente todas
			} catch {
				toast.error('Erro ao carregar dados iniciais');
			}
		})();
	}, []);

	// sempre que mudar clientId no formulário, filtra as plataformas
	useEffect(() => {
		setFilteredPlatforms(
			newSispat.clientId
				? allPlatforms.filter(p => p.client_id === newSispat.clientId)
				: allPlatforms
		);
	}, [newSispat.clientId, allPlatforms]);

	const handleFetchSispat = async () => {
		setHasSearched(true);
		if (selectedWorkerId !== '') {
			try {
				const list = await apiGetSispatByWorker(selectedWorkerId as number);
				setSispats(list);
			} catch {
				toast.error('Erro ao carregar SISPAT do trabalhador');
			}
		}
	};

	const handleAddSispat = async () => {
		try {
			await apiCreateSispat({
				workerId: selectedWorkerId as number,
				clientId: newSispat.clientId,
				platformId: newSispat.platformId,
				sispatNumber: newSispat.sispatNumber,
				status: newSispat.status,
				statusDescription: newSispat.statusDescription || null,
			});
			toast.success('SISPAT criado com sucesso');
			handleFetchSispat();
			setNewSispat({
				clientId: 0,
				sispatNumber: 0,
				status: true,
				statusDescription: '',
				platformId: null,
			});
		} catch {
			toast.error('Erro ao criar SISPAT');
		}
	};

	const handleUpdateSispat = async (entry: ISispatOutput) => {
		try {
			await apiUpdateSispat(entry.id, {
				workerId: selectedWorkerId as number,
				clientId: entry.clientId,
				platformId: entry.platformId,
				sispatNumber: entry.sispatNumber,
				status: entry.status,
				statusDescription: entry.statusDescription,
			});
			toast.success('SISPAT atualizado com sucesso');
			handleFetchSispat();
		} catch {
			toast.error('Erro ao atualizar SISPAT');
		}
	};

	const handleDownloadSispatExcel = async () => {
		try {
			const all = await apiGetAllSispat();
			if (!all.length) {
				toast.error('Nenhum SISPAT disponível para exportação.');
				return;
			}
			const tableData: any[][] = [
				[
					'Trabalhador',
					'CPF',
					'Cliente',
					'Plataforma',
					'Número SISPAT',
					'Status',
					'Descrição',
				],
				...all.map(item => [
					item.workerName, // ou se tiver descrição: item.workerName
					item.cpf,
					item.clientDescription,
					item.platformDescription,
					item.sispatNumber,
					item.status ? 'Ativo' : 'Inativo',
					item.statusDescription || '',
				]),
			];
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.aoa_to_sheet(tableData);
			XLSX.utils.book_append_sheet(wb, ws, 'SISPATs');
			XLSX.writeFile(wb, 'SISPATs.xlsx');
		} catch {
			toast.error('Erro ao exportar SISPATs.');
		}
	};

	return (
		<Grid container spacing={3}>
			{/* Seleção do Trabalhador */}
			<Grid item xs={12}>
				<Paper elevation={5} style={{ padding: 20 }}>
					<Typography variant="h5" component="h2" style={{ marginBottom: 30 }}>
						<strong>SISPAT</strong> – Associação Trabalhador × Cliente
					</Typography>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={4}>
							<FormControl fullWidth variant="outlined">
								<InputLabel id="worker-select-label">Trabalhador</InputLabel>
								<Select
									labelId="worker-select-label"
									value={selectedWorkerId}
									onChange={e => {
										const id = Number(e.target.value);
										setSelectedWorkerId(id);
										// limpa estado
										setHasSearched(false);
										setSispats([]);
										setNewSispat({
											clientId: 0,
											sispatNumber: 0,
											status: true,
											statusDescription: '',
											platformId: null,
										});
									}}
									label="Trabalhador"
								>
									{workerNames.map(w => (
										<MenuItem key={w.id} value={w.id}>
											{w.worker_name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item>
							<Button
								variant="contained"
								// size="small"
								style={{
									background: '#e5e5e5',
									color: 'black',
								}}
								onClick={handleFetchSispat}
							>
								<strong>Pesquisar</strong>
							</Button>
						</Grid>
						<Grid item>
							<Tooltip title="Exportar SISPATs">
								<IconButton
									onClick={handleDownloadSispatExcel}
									sx={{
										backgroundColor: '#007eb8',
										color: 'white',
										width: 30,
										height: 30,
										borderRadius: '4px',
										'&:hover': { backgroundColor: '#006a9e' },
									}}
								>
									<Download />
								</IconButton>
							</Tooltip>
						</Grid>
					</Grid>
				</Paper>
			</Grid>

			{/* Novo SISPAT */}
			{hasSearched && selectedWorkerId !== '' && (
				<Grid item xs={12}>
					<Paper elevation={5} style={{ padding: 20 }}>
						<Typography variant="h6" style={{ marginBottom: 30 }}>
							Novo SISPAT
						</Typography>
						<Grid container spacing={2} alignItems="center">
							<Grid item xs={6} sm={1.2}>
								<TextField
									fullWidth
									type="number"
									label="Número"
									value={newSispat.sispatNumber}
									onChange={e =>
										setNewSispat(prev => ({
											...prev,
											sispatNumber: Number(e.target.value),
										}))
									}
								/>
							</Grid>
							<Grid item xs={6} sm={2}>
								<FormControl fullWidth>
									<InputLabel id="new-client-select-label">Cliente</InputLabel>
									<Select
										labelId="new-client-select-label"
										value={newSispat.clientId}
										onChange={e =>
											setNewSispat(prev => ({
												...prev,
												clientId: Number(e.target.value),
											}))
										}
										label="Cliente"
									>
										{allClients.map(c => (
											<MenuItem key={c.id} value={c.id}>
												{c.client_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							{/* Plataforma filtrada */}
							<Grid item xs={12} sm={3}>
								<FormControl fullWidth>
									<InputLabel id="platform-select-label">Plataforma</InputLabel>
									<Select
										labelId="platform-select-label"
										value={
											newSispat.platformId !== null
												? String(newSispat.platformId)
												: ''
										}
										onChange={e =>
											setNewSispat(prev => ({
												...prev,
												platformId:
													e.target.value === '' ? null : Number(e.target.value),
											}))
										}
										label="Plataforma"
									>
										{filteredPlatforms.map(p => (
											<MenuItem key={p.id} value={p.id}>
												{p.platform_name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={6} sm={1}>
								<FormControlLabel
									control={
										<Switch
											checked={newSispat.status}
											onChange={e =>
												setNewSispat(prev => ({
													...prev,
													status: e.target.checked,
												}))
											}
										/>
									}
									label="Ativo"
								/>
							</Grid>
							<Grid item xs={12} sm={6.2}>
								<TextField
									fullWidth
									label="Descrição do Status"
									multiline
									rows={2}
									value={newSispat.statusDescription}
									onChange={e =>
										setNewSispat(prev => ({
											...prev,
											statusDescription: e.target.value,
										}))
									}
								/>
							</Grid>
							{!hideAddButton && (
								<Grid item>
									<Button
										variant="contained"
										sx={{
											mt: 1,
											backgroundColor: green[500],
											'&:hover': { backgroundColor: green[700] },
										}}
										onClick={handleAddSispat}
									>
										<strong>Adicionar</strong>
									</Button>
								</Grid>
							)}
						</Grid>
					</Paper>
				</Grid>
			)}

			{/* Lista de SISPATs */}
			{sispats.length > 0 && (
				<Grid item xs={12}>
					<Paper elevation={5} style={{ padding: 20 }}>
						<Typography variant="h6">SISPATs Associados</Typography>
						{sispats.map((entry, idx) => {
							// filtra plataformas por clientId de cada entry
							const entryPlats = allPlatforms.filter(
								p => p.client_id === entry.clientId
							);

							return (
								<React.Fragment key={entry.id}>
									<Grid
										container
										spacing={2}
										alignItems="center"
										sx={{ mt: idx === 0 ? 2 : 0, mb: 2 }}
									>
										<Grid item xs={6} sm={1.2}>
											<TextField
												fullWidth
												type="number"
												label="Número"
												value={entry.sispatNumber}
												onChange={e => {
													const val = Number(e.target.value);
													setSispats(prev =>
														prev.map(s =>
															s.id === entry.id
																? { ...s, sispatNumber: val }
																: s
														)
													);
												}}
											/>
										</Grid>
										<Grid item xs={6} sm={2}>
											<FormControl fullWidth>
												<InputLabel id={`client-select-label-${entry.id}`}>
													Cliente
												</InputLabel>
												<Select
													labelId={`client-select-label-${entry.id}`}
													value={entry.clientId}
													onChange={e => {
														const newClientId = Number(e.target.value);
														const newClient = allClients.find(
															c => c.id === newClientId
														)!;
														setSispats(prev =>
															prev.map(s =>
																s.id === entry.id
																	? {
																			...s,
																			clientId: newClientId,
																			clientDescription: newClient.client_name,
																	  }
																	: s
															)
														);
													}}
													label="Cliente"
												>
													{allClients.map(c => (
														<MenuItem key={c.id} value={c.id}>
															{c.client_name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										{/* Plataforma filtrada por entry.clientId */}
										<Grid item xs={12} sm={3}>
											<FormControl fullWidth>
												<InputLabel id={`platform-label-${entry.id}`}>
													Plataforma
												</InputLabel>
												<Select
													labelId={`platform-label-${entry.id}`}
													value={
														entry.platformId !== null &&
														entry.platformId !== undefined
															? String(entry.platformId)
															: ''
													}
													onChange={e => {
														const id =
															e.target.value === ''
																? null
																: Number(e.target.value);
														setSispats(prev =>
															prev.map(s =>
																s.id === entry.id ? { ...s, platformId: id } : s
															)
														);
													}}
													label="Plataforma"
												>
													{entryPlats.map(p => (
														<MenuItem key={p.id} value={p.id}>
															{p.platform_name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={6} sm={1}>
											<FormControlLabel
												control={
													<Switch
														checked={entry.status}
														onChange={e =>
															setSispats(prev =>
																prev.map(s =>
																	s.id === entry.id
																		? { ...s, status: e.target.checked }
																		: s
																)
															)
														}
													/>
												}
												label="Ativo"
											/>
										</Grid>
										<Grid item xs={12} sm={6.2}>
											<TextField
												fullWidth
												label="Descrição do Status"
												multiline
												rows={2}
												value={entry.statusDescription || ''}
												onChange={e =>
													setSispats(prev =>
														prev.map(s =>
															s.id === entry.id
																? { ...s, statusDescription: e.target.value }
																: s
														)
													)
												}
											/>
										</Grid>
										{!hideUpdateButton && (
											<Grid item>
												<Button
													variant="contained"
													onClick={() => handleUpdateSispat(entry)}
												>
													Atualizar
												</Button>
											</Grid>
										)}
									</Grid>
									{idx < sispats.length - 1 && (
										<Divider
											sx={{
												width: '100%',
												height: 1,
												backgroundColor: 'rgba(0,0,0,0.3)',
												mt: 3, // margem superior maior
												mb: 6, // margem inferior maior
											}}
										/>
									)}
								</React.Fragment>
							);
						})}
					</Paper>
				</Grid>
			)}
		</Grid>
	);
};

export default SispatModule;
